import { formatClock, rangeLabel } from '../lib/format'
import { useI18n } from '../lib/i18n'

interface Props {
  weekStart: string
  weekEnd: string
  offset: number
  loading: boolean
  /** ISO instant the shown data was assembled, if anything has loaded yet. */
  updatedAt?: string
  /** Rendered between the title and the arrows, so the calendar gets one
   *  toolbar instead of two stacked control rows. */
  children?: React.ReactNode
  onOffset: (next: number) => void
}

export function WeekNav({
  weekStart,
  weekEnd,
  offset,
  loading,
  updatedAt,
  children,
  onOffset,
}: Props) {
  const { t, locale } = useI18n()
  const named = offset === 0 || offset === 1
  const range = rangeLabel(weekStart, weekEnd, locale)

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold sm:text-base">
          {offset === 0 ? t.thisWeek : offset === 1 ? t.nextWeek : range}
        </h2>
        {named && (
          <span className="nums text-xs" style={{ color: 'var(--text-muted)' }}>
            {range}
          </span>
        )}
        {loading && (
          <span
            className="size-3 animate-spin rounded-full border-2 border-transparent"
            style={{
              borderTopColor: 'var(--color-brand-400)',
              borderRightColor: 'var(--color-brand-400)',
            }}
            aria-label={t.loading}
          />
        )}
      </div>

      <div className="order-3 sm:order-2">{children}</div>

      <div className="order-2 ml-auto flex items-center gap-1 sm:order-3">
        {updatedAt && !loading && (
          <span
            className="nums mr-1.5 hidden text-xs sm:inline"
            style={{ color: 'var(--text-faint)' }}
          >
            {t.updated} {formatClock(updatedAt, locale)}
          </span>
        )}
        <NavButton label={t.prevWeek} disabled={offset <= 0} onClick={() => onOffset(offset - 1)}>
          ‹
        </NavButton>
        <button
          type="button"
          onClick={() => onOffset(0)}
          disabled={offset === 0}
          className="rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
        >
          {t.today}
        </button>
        <NavButton
          label={t.nextWeekLabel}
          disabled={offset >= 3}
          onClick={() => onOffset(offset + 1)}
        >
          ›
        </NavButton>
      </div>
    </div>
  )
}

function NavButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-lg border text-base transition-colors disabled:opacity-30"
      style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
    >
      {children}
    </button>
  )
}
